import { Box, Stack, Typography } from '@mui/material';
import { type ObjectLiteral, useSubmission, useUpdateUwQuestions } from 'api/submission';
import { APPLICATION_STEPS, GOOGLE_TAG_EVENTS, META } from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { type Field, QuestionEngine, useQuestionEngine } from 'components/lib/QuestionEngine';
import { WarningBanner } from 'components/lib/WarningBanner';
import { StepFooterActions } from 'components/StepFooterActions';
import { containsMultipleNames, pushToGoogleDataLayer, trackEventFullStory } from 'helpers/utils';
import { isEmpty, isEqual, pick } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';

const {
  label,
  fields: initialFields,
}: {
  label: string;
  fields: Field[];
} = APPLICATION_STEPS.legal;

const Legal = () => {
  const { id } = useParams() as { id: string };
  const { data: submission } = useSubmission(id);
  const { mutateAsync: saveUnderwritingQuestions } = useUpdateUwQuestions(id);
  const { setFullscreenLoading } = useFullscreenLoader();
  const { validateFormik, updateState, ...questionEngineProps } = useQuestionEngine({
    fields: initialFields,
    wrapperSx: { mb: 3 },
  });
  const { state, ref: questionEngineRef } = questionEngineProps;

  const submissionState: ObjectLiteral = {
    effective_date: submission?.effective_date,
    ...submission?.data.policy_data,
    ...submission?.data.uw_questions,
  };

  // When backend submission data changed, sync it to question engine state
  useEffect(() => {
    if (!isEmpty(submission?.data)) {
      updateState(submissionState);
    }
  }, [submission?.data]);

  const handleNext = async () => {
    const isValid = await validateFormik();

    if (!isValid) {
      return false;
    }

    const renderedQuestionCodes =
      questionEngineRef?.current?.renderedQuestions()?.map((question) => question.code) || [];

    /**
     * if the backend submission state is same as the question engine state,
     * let changing the step without saving
     */
    if (isEqual(pick(state, renderedQuestionCodes), pick(submissionState, renderedQuestionCodes))) {
      return true;
    }

    try {
      setFullscreenLoading(true, 'Saving the answers');

      await saveUnderwritingQuestions({
        id,
        body: pick(state, renderedQuestionCodes),
      });

      if (isEmpty(submissionState?.entityType)) {
        pushToGoogleDataLayer({ event: GOOGLE_TAG_EVENTS.LEAD_LEGACY });
        trackEventFullStory(GOOGLE_TAG_EVENTS.LEAD_LEGACY);
      }

      return true;
    } catch (_error) {
      displayBackendErrorMessage(_error);
      return false;
    } finally {
      setFullscreenLoading(false);
    }
  };

  // Users sometimes try to cover multiple businesses with one policy which is not allowed
  const showMultipleBusinessesWarning = useMemo(() => {
    return containsMultipleNames(state?.businessName) || containsMultipleNames(state?.dba);
  }, [state?.businessName, state?.dba]);

  return (
    <Stack sx={{ flex: 1 }}>
      <Typography variant="h1" sx={{ mb: 3 }}>
        {label}
      </Typography>

      {showMultipleBusinessesWarning && (
        <Box sx={{ mb: 2 }}>
          <WarningBanner>
            <Typography>
              It looks like you may be trying to add more than one business to this policy. To add
              additional businesses, please call us at {META.PRIORITY_CONTACT_NUMBER}.
            </Typography>
          </WarningBanner>
        </Box>
      )}

      <QuestionEngine {...questionEngineProps} />

      <StepFooterActions onNext={handleNext} />
    </Stack>
  );
};

export default Legal;
